import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DollarSign, Receipt, ArrowLeft, User, AlertCircle, CheckCircle, Lock, Clock } from 'lucide-react';
import api from '@/api/api';
import PaymentBreakdown from '@/components/PaymentBreakdown';
import { generateFeePaymentReceipt } from '@/utils/feePaymentReceiptGenerator';
import Select from 'react-select';

const PayFee = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const studentIdParam = searchParams.get('student_id');

  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(studentIdParam || '');
  const [installmentData, setInstallmentData] = useState(null);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const [paymentData, setPaymentData] = useState({
    amount_paid: 0,
    payment_modes: [],
    waive_fine: false,
    remarks: '',
    payment_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchStudents();
    loadCollegeInfo();
    if (studentIdParam) {
      setSelectedStudentId(studentIdParam);
      fetchInstallments(studentIdParam);
    }
  }, [studentIdParam]);

  const loadCollegeInfo = async () => {
    try {
      const response = await api.get('/tenants/my-college/settings');
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get(`/students/dropdown`);
      const studentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const approvedStudents = studentsData.filter(s => s.is_approved);
      setStudents(approvedStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  // Create options for react-select with searchable fields
  const studentOptions = students.map(student => ({
    value: student.id,
    label: `${student.admission_number} - ${student.full_name}`,
    student: student,
    searchText: `${student.admission_number} ${student.full_name} ${student.phone || ''} ${student.email || ''} ${student.father_name || ''} ${student.mother_name || ''}`.toLowerCase()
  }));

  // Custom filter function for react-select
  const customFilterOption = (option, inputValue) => {
    if (!inputValue) return true;
    const searchTerms = inputValue.toLowerCase().trim().split(' ');
    return searchTerms.every(term => option.data.searchText.includes(term));
  };

  const fetchInstallments = async (studentId) => {
    try {
      setLoading(true);
      const response = await api.get(`/installment-payments/student/${studentId}/installments`);
      setInstallmentData(response.data);
    } catch (error) {
      console.error('Error fetching installments:', error);
      alert('Error fetching installment data');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentChange = (studentId) => {
    setSelectedStudentId(studentId);
    if (studentId) {
      fetchInstallments(studentId);
      setShowPaymentForm(false);
      setSelectedInstallment(null);
    } else {
      setInstallmentData(null);
      setShowPaymentForm(false);
      setSelectedInstallment(null);
    }
  };

  const handlePayInstallment = (installment) => {
    setSelectedInstallment(installment);
    setPaymentData({
      amount_paid: installment.total_due,
      payment_modes: [],
      waive_fine: false,
      remarks: ''
    });
    setShowPaymentForm(true);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (loading) return; // hard guard against rapid double-click double payments

    if (!paymentData.payment_modes || paymentData.payment_modes.length === 0) {
      alert('Please add at least one payment mode');
      return;
    }

    const totalPaid = paymentData.payment_modes.reduce((sum, mode) => sum + parseFloat(mode.amount || 0), 0);
    if (Math.abs(totalPaid - paymentData.amount_paid) > 0.01) {
      alert(`Payment breakdown total (₹${totalPaid.toLocaleString('en-IN')}) must equal payment amount (₹${paymentData.amount_paid.toLocaleString('en-IN')})`);
      return;
    }

    if (paymentData.amount_paid <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    const maxAmount = paymentData.waive_fine ? selectedInstallment.balance_amount : selectedInstallment.total_due;
    if (paymentData.amount_paid > maxAmount) {
      alert(`Payment amount cannot exceed ₹${maxAmount.toLocaleString('en-IN')}`);
      return;
    }

    setLoading(true);
    try {
      const student = students.find(s => s.id === selectedStudentId);
      
      const payment = {
        installment_id: selectedInstallment.id,
        student_id: selectedStudentId,
        student_name: student.full_name,
        admission_no: student.admission_number,
        course_name: student.course_name,
        session: student.session,
        amount_paid: parseFloat(paymentData.amount_paid),
        payment_modes: paymentData.payment_modes,
        waive_fine: paymentData.waive_fine,
        total_fee: installmentData.fee_setting.total_fee || 0,
        previously_paid: 0,
        outstanding_balance: 0,
        remarks: paymentData.remarks,
        collected_by: 'Admin',
        payment_date: paymentData.payment_date
      };

      const response = await api.post(
        `/installment-payments/pay-installment`,
        payment
      );
      
      alert(`Payment recorded successfully!\nPrincipal: ₹${response.data.principal_paid}\nFine: ₹${response.data.fine_paid}${paymentData.waive_fine ? ' (Waived)' : ''}`);
      
      fetchInstallments(selectedStudentId);
      
      setPaymentData({
        amount_paid: 0,
        payment_modes: [],
        waive_fine: false,
        remarks: '',
        payment_date: new Date().toISOString().split('T')[0]
      });
      setShowPaymentForm(false);
      setSelectedInstallment(null);

      const receiptData = {
        receipt_no: response.data.receipt_no,
        payment_id: response.data.payment_id,
        student_name: student.full_name,
        admission_no: student.admission_number,
        course_name: student.course_name,
        session: student.session,
        payment_date: paymentData.payment_date,
        amount_paid: paymentData.amount_paid,
        payment_modes: paymentData.payment_modes,
        total_fee: response.data.total_fee || installmentData.fee_setting?.total_fee || 0,
        discount_amount: response.data.discount_amount || student.discount_amount || 0,
        scholarship_amount: response.data.scholarship_amount || student.scholarship_amount || 0,
        previously_paid: response.data.previously_paid || 0,
        total_paid_after: response.data.total_paid_after || 0,
        outstanding_balance: response.data.outstanding_balance ?? response.data.balance_remaining ?? 0,
        payment_for: `Installment ${selectedInstallment.installment_number}`,
        remarks: `Principal: '${response.data.principal_paid}, Fine: '${response.data.fine_paid}${paymentData.waive_fine ? ' (Waived)' : ''}`
      };
      await generateFeePaymentReceipt(receiptData, collegeInfo);

    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Error recording payment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Paid': return 'bg-green-50 border-green-200';
      case 'Partially Paid': return 'bg-yellow-50 border-yellow-200';
      case 'Overdue': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Paid': return <CheckCircle className="text-green-600" size={20} />;
      case 'Partially Paid': return <Clock className="text-yellow-600" size={20} />;
      case 'Overdue': return <AlertCircle className="text-red-600" size={20} />;
      default: return <Clock className="text-gray-600" size={20} />;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Installment Payment</h1>
            <p className="text-sm text-gray-600">Pay semester/installment-wise fees with late fine management</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <User className="inline mr-2" size={18} />
          Select Student *
        </label>
        <Select
          value={studentOptions.find(opt => opt.value === selectedStudentId) || null}
          onChange={(selectedOption) => handleStudentChange(selectedOption ? selectedOption.value : '')}
          options={studentOptions}
          filterOption={customFilterOption}
          placeholder="Search by name, mobile, email, admission number, or parent's name..."
          isClearable
          isSearchable
          className="react-select-container"
          classNamePrefix="react-select"
          styles={{
            control: (base) => ({
              ...base,
              minHeight: '42px',
              borderColor: '#d1d5db',
              '&:hover': {
                borderColor: '#9ca3af'
              }
            }),
            menu: (base) => ({
              ...base,
              zIndex: 50
            })
          }}
          formatOptionLabel={(option) => (
            <div>
              <div className="font-semibold text-gray-900">{option.label}</div>
              <div className="text-sm text-gray-600">
                {option.student.course_name} | {option.student.phone} | {option.student.email}
              </div>
              {(option.student.father_name || option.student.mother_name) && (
                <div className="text-xs text-gray-500">
                  {option.student.father_name && `Father: ${option.student.father_name}`}
                  {option.student.father_name && option.student.mother_name && ' | '}
                  {option.student.mother_name && `Mother: ${option.student.mother_name}`}
                </div>
              )}
            </div>
          )}
        />
        <p className="text-xs text-gray-500 mt-2">
          💡 Tip: You can search by student name, admission number, mobile, email, father's name, or mother's name
        </p>
      </div>

      {installmentData && installmentData.has_installments && (
        <div className="space-y-4">
          {installmentData.installments.map((inst, index) => (
            <div key={index} className={`border-2 rounded-lg p-6 ${getStatusColor(inst.status)}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    {getStatusIcon(inst.status)}
                    <h3 className="text-lg font-bold text-gray-900">
                      Installment {inst.installment_number} - ₹{inst.amount.toLocaleString('en-IN')}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      inst.status === 'Paid' ? 'bg-green-100 text-green-800' :
                      inst.status === 'Partially Paid' ? 'bg-yellow-100 text-yellow-800' :
                      inst.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {inst.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-600">Due Date</p>
                      <p className="text-sm font-medium text-gray-900">{inst.due_date}</p>
                    </div>
                    {inst.paid_date && (
                      <div>
                        <p className="text-xs text-gray-600">Payment Date</p>
                        <p className="text-sm font-medium text-blue-600">{inst.paid_date}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-600">Amount</p>
                      <p className="text-sm font-medium text-gray-900">₹{inst.amount.toLocaleString('en-IN')}</p>
                    </div>
                    {inst.paid_amount > 0 && (
                      <div>
                        <p className="text-xs text-gray-600">Paid Amount</p>
                        <p className="text-sm font-medium text-green-600">₹{inst.paid_amount.toLocaleString('en-IN')}</p>
                      </div>
                    )}
                    {inst.balance_amount > 0 && (
                      <div>
                        <p className="text-xs text-gray-600">Balance</p>
                        <p className="text-sm font-medium text-orange-600">₹{inst.balance_amount.toLocaleString('en-IN')}</p>
                      </div>
                    )}
                  </div>

                  {inst.fine_amount > 0 && (
                    <div className="bg-red-100 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-sm font-medium text-red-800">
                        ⚠️ Late Fine: ₹{inst.fine_amount.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Total Due (with fine): ₹{inst.total_due.toLocaleString('en-IN')}
                      </p>
                    </div>
                  )}
                </div>

                <div className="ml-4">
                  {inst.status === 'Paid' ? (
                    <div className="text-center">
                      <CheckCircle className="text-green-600 mx-auto mb-2" size={32} />
                      <p className="text-sm font-medium text-green-600">Paid</p>
                    </div>
                  ) : inst.can_pay ? (
                    <button
                      onClick={() => handlePayInstallment(inst)}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      {inst.status === 'Partially Paid' ? 'Pay Balance' : 'Pay Now'}
                    </button>
                  ) : (
                    <div className="text-center">
                      <Lock className="text-gray-400 mx-auto mb-2" size={32} />
                      <p className="text-xs text-gray-500">Locked</p>
                      <p className="text-xs text-gray-400">Clear previous first</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {installmentData && !installmentData.has_installments && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-center">
          No installment plan found for this student. Please set up installments in Fee Settings.
        </div>
      )}

      {showPaymentForm && selectedInstallment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Pay Installment {selectedInstallment.installment_number}
              </h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Balance Amount:</p>
                    <p className="font-bold text-gray-900">₹{selectedInstallment.balance_amount.toLocaleString('en-IN')}</p>
                  </div>
                  {selectedInstallment.fine_amount > 0 && (
                    <div>
                      <p className="text-gray-600">Late Fine:</p>
                      <p className="font-bold text-red-600">₹{selectedInstallment.fine_amount.toLocaleString('en-IN')}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-600">Total Due:</p>
                    <p className="font-bold text-blue-600">₹{selectedInstallment.total_due.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmitPayment} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                  <input
                    type="date"
                    data-testid="payment-date-input"
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default: Today. Change for backdated entry.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                    <input
                      type="number"
                      value={paymentData.amount_paid}
                      onChange={(e) => {
                        const amount = parseFloat(e.target.value) || 0;
                        setPaymentData({...paymentData, amount_paid: amount});
                      }}
                      placeholder="Enter amount"
                      min="0"
                      step="0.01"
                      required
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Max: ₹{(paymentData.waive_fine ? selectedInstallment.balance_amount : selectedInstallment.total_due).toLocaleString('en-IN')}
                  </p>
                </div>

                {selectedInstallment.fine_amount > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={paymentData.waive_fine}
                        onChange={(e) => {
                          const waived = e.target.checked;
                          setPaymentData({
                            ...paymentData,
                            waive_fine: waived,
                            amount_paid: waived ? selectedInstallment.balance_amount : selectedInstallment.total_due
                          });
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Waive Late Fine (-₹{selectedInstallment.fine_amount.toLocaleString('en-IN')}) - Admin Only
                      </span>
                    </label>
                  </div>
                )}

                {paymentData.amount_paid > 0 && (
                  <PaymentBreakdown
                    totalAmount={paymentData.amount_paid}
                    paymentModes={paymentData.payment_modes}
                    onChange={(modes) => setPaymentData({...paymentData, payment_modes: modes})}
                    label="Payment Breakdown *"
                  />
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (Optional)</label>
                  <textarea
                    value={paymentData.remarks}
                    onChange={(e) => setPaymentData({...paymentData, remarks: e.target.value})}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Add any notes..."
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                  >
                    <Receipt size={18} />
                    {loading ? 'Processing...' : 'Record Payment & Generate Receipt'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPaymentForm(false);
                      setSelectedInstallment(null);
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {!selectedStudentId && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-center">
          Please select a student to view installment schedule
        </div>
      )}
    </div>
  );
};

export default PayFee;
